import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useT } from '@/i18n'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'
import { isSuperAdmin } from '@/lib/permissions'
import {
  fetchStaffMember,
  fetchRoleCatalog,
  setStaffRole,
  type AdminStaff,
  type AdminRoleDef,
} from '@/lib/adapters'
import { InfoRow, MonoValue, RelativeTime } from '@/components/DataDisplay'
import { StatusBadge, Tag } from '@/components/StatusBadge'
import { ErrorState, Skeleton } from '@/components/States'

/**
 * A23.02 — Xodim kartasi. /admin/staff (id bo'yicha) + /admin/roles (rol huquqlari).
 * Superadmin rolni o'zgartira oladi (POST /admin/staff/:id/role). StaffScreen
 * sub-route'i — o'z PageHeader'isiz (umumiy A23 sarlavhasi ostida).
 */
export function StaffCard() {
  const t = useT()
  const { identity } = useAuth()
  const { toast } = useToast()
  const { id = '' } = useParams()
  const isSuper = isSuperAdmin(identity)
  const [staff, setStaff] = useState<AdminStaff | null>(null)
  const [roles, setRoles] = useState<AdminRoleDef[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    Promise.all([fetchStaffMember(Number(id), ac.signal), fetchRoleCatalog(ac.signal)])
      .then(([s, r]) => {
        if (!alive) return
        setStaff(s)
        setRoles(r)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [id, reload])

  const roleDef = useMemo(() => roles.find((r) => r.role === staff?.role), [roles, staff])
  const assignable = useMemo(() => roles.filter((r) => r.assignable).map((r) => r.role), [roles])

  const changeRole = async (role: string) => {
    if (!staff) return
    const prev = staff.role
    setStaff({ ...staff, role })
    try {
      await setStaffRole(staff.id, role)
      toast(t.staff.roleSaved, { tone: 'success' })
    } catch (e) {
      setStaff({ ...staff, role: prev })
      toast(t.staff.roleFailed, {
        tone: 'error',
        ...(e instanceof Error && e.message ? { description: e.message } : {}),
      })
    }
  }

  return (
    <div className="min-h-0 flex-1 overflow-auto p-3">
      <div className="mb-3">
        <Link
          to="/staff"
          className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
        >
          <ChevronLeft size={13} />
          {t.staff.title}
        </Link>
      </div>
      {loading ? (
        <div className="max-w-xl space-y-2 rounded-lg border border-border-default bg-surface-2 p-4">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-4 w-2/3" />
          ))}
        </div>
      ) : error ? (
        <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
      ) : !staff ? (
        <ErrorState title={t.states.notFoundTitle} />
      ) : (
        <div className="grid max-w-3xl gap-3 lg:grid-cols-2">
          <div className="rounded-lg border border-border-default bg-surface-2 p-4">
            <div className="mb-3">
              <StatusBadge status={staff.is_active ? 'active' : 'deleted'} />
            </div>
            <Field label={t.col.id}>
              <MonoValue value={String(staff.id)} />
            </Field>
            <Field label={t.account.username}>{staff.username}</Field>
            <Field label={t.account.role}>
              {isSuper ? (
                <select
                  value={staff.role}
                  onChange={(e) => changeRole(e.target.value)}
                  className="rounded-md border border-border-default bg-surface-1 px-2 py-1 font-mono text-xs text-primary"
                >
                  {(assignable.includes(staff.role) ? assignable : [staff.role, ...assignable]).map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="font-mono text-sm">{staff.role}</span>
              )}
            </Field>
            <Field label={t.staff.lastLogin}>
              {staff.last_login_at ? <RelativeTime value={staff.last_login_at} /> : '—'}
            </Field>
            <Field label={t.col.registered}>
              <RelativeTime value={staff.created_at} />
            </Field>
          </div>

          <div className="rounded-lg border border-border-default bg-surface-2 p-4">
            <div className="mb-2 text-sm font-medium text-secondary">
              {t.staff.permissionMatrix} · {roleDef?.permission_count ?? 0}
            </div>
            {roleDef?.all ? (
              <Tag>{t.staff.allPerms}</Tag>
            ) : roleDef && roleDef.permissions.length ? (
              <div className="flex flex-wrap gap-1">
                {roleDef.permissions.map((p) => (
                  <span key={p} className="rounded-sm border border-border-subtle px-1.5 py-0.5 font-mono text-xs text-secondary">
                    {p}
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-sm text-tertiary">—</span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <InfoRow label={label}>{children}</InfoRow>
}
